import { EventTypes } from 'redux-segment';

import addUserRole from 'actions/user/addUserRole.action';
import SEGMENT from 'config/segment.singleton';
import USER_ROLE from 'constants/role';
import { refreshCognitoUserToken, selectAccessIds, selectManageIds } from 'utils/cognito.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import logger from 'utils/logger.util';
import parseJson from 'utils/parsers/parseJson';
import isNotFn from 'utils/predicates/function/isNotFn.util';

import { PERMISSION_ACCESS_SUCCESS, SET_CURRENT_SALON } from 'salon/constants';

const L = logger('setCurrentSalon');

const runAsync = (options, fn) => {
  const FTAG = 'runAsync()';
  const tag = fromAnyToTrimmed(options?.tag);

  if (isNotFn(fn)) L.error(FTAG, tag, 'Not a function:', fn);

  let promise;
  try {
    promise = fn();

    if (promise instanceof Promise) promise.catch((e) => L.warn(FTAG, tag, options?.onCatch, e));
    else L.warn(FTAG, tag, 'Returned value is not a promise:', promise);
  } catch (e) {
    L.error(FTAG, tag, 'Returned value is not a promise. Function threw an error instead:', e);
  }

  return promise;
};

const setCompanyForSalonId = (options) => {
  const id = options?.salonId;
  if (!id) return void L.info('Not setting invalid salon id:', id);

  const ids = options?.jsonIds ? [...(parseJson(options?.jsonIds) ?? void 1)] : [];
  if (!ids.includes(id)) return void L.info('Salon id:', id, 'not found in allowed salon ids:', ids);

  SEGMENT.setCompany({ id });

  try {
    options?.dispatch?.({ type: SET_CURRENT_SALON, salon: id, meta: { analytics: EventTypes.track } });
  } catch (e) {
    L.warn('setCompanyForSalonId()', e);
  }
};

const setCurrentSalon = (options) => {
  const { salonId, userRole, isSupport } = options ?? {};

  if (USER_ROLE.manager !== userRole && USER_ROLE.receptionist !== userRole) {
    L.warn('()', 'Invalid userRole:', userRole, 'for salonId:', salonId);
  }

  if (!salonId) return { type: SET_CURRENT_SALON, salon: null, meta: { analytics: EventTypes.track } };

  return (dispatch, getState) => {
    // work out whether we need to try to set permissions
    if (userRole === USER_ROLE.manager) {
      setCompanyForSalonId({ salonId, dispatch, jsonIds: selectManageIds(getState()) });
    } else if (userRole === USER_ROLE.receptionist) {
      setCompanyForSalonId({ salonId, dispatch, jsonIds: selectAccessIds(getState()) });
    }

    if (!salonId) return;

    const promise = runAsync(
      {
        tag: 'addUserRole',
        onCatch: 'Failed setting permission',
      },
      async () => {
        const addUserRoleResult = await dispatch(addUserRole({ salonId, userRole }));
        if (PERMISSION_ACCESS_SUCCESS !== addUserRoleResult?.type) {
          L.warn('()', 'Failed setting permission, addUserRoleResult:', addUserRoleResult);
        }
      },
    );

    (isSupport ? promise : Promise.resolve()).then(() => {
      runAsync(
        {
          tag: 'refreshCognitoUserToken',
          onCatch: 'Failed refreshing token',
        },
        async () => {
          await refreshCognitoUserToken({ state: getState(), dispatch });
          if (salonId) SEGMENT.setCompany({ id: salonId });
          dispatch({ type: SET_CURRENT_SALON, salon: salonId, meta: { analytics: EventTypes.track } });
        },
      );
    });
  };
};

// noinspection JSUnusedGlobalSymbols
export default setCurrentSalon;
